import React, { PropsWithChildren, useMemo, useState } from 'react';
import type { Note } from 'sr-types/lib/search/v1/graphql';
import { EntitySecurityProvider, type EntityName } from '../../../auth/Security';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import type { ShareSettingsViewModel } from '../../../share';
import { ShareSettingsProvider } from '../../../share';
import { client } from '../../data/notesGraphqlApi';
import type { NotesContextValue } from '../../types';
import { NotesContext } from '../context/NotesContext';

export type NotesProviderProps = PropsWithChildren<{
    entity: EntityName;
    disableTranslation?: boolean;
}>;

export function NotesProvider({ entity, children, disableTranslation }: NotesProviderProps) {
    const [notes, setNotes] = useState<Note[]>([]);

    const [pendingShareSettingsForNewNote, setPendingShareSettingsForNewNote] = useState<ShareSettingsViewModel | null>(
        null
    );
    const [shareSettingsForNoteBeingEdited, setShareSettingsForNoteBeingEdited] =
        useState<ShareSettingsViewModel | null>(null);
    const [sharePanelOpen, setSharePanelOpen] = useState(false);
    const [openShareForNoteId, setOpenShareForNoteId] = useState<string | null>(null);
    const [newNoteDraft, setNewNoteDraft] = useState<Note | null>(null);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [isInEditMode, setIsInEditMode] = useState(false);

    const selectedNote = useMemo(() => {
        let selectedNote: Note | null = null;
        if (editingNote) {
            selectedNote = editingNote;
        }
        if (!selectedNote && openShareForNoteId) {
            selectedNote = notes.find((note) => note.id === openShareForNoteId) ?? null;
        }
        return selectedNote;
    }, [editingNote, notes, openShareForNoteId]);

    const value: NotesContextValue = useMemo(
        () => ({
            entity,
            pendingShareSettingsForNewNote,
            setPendingShareSettingsForNewNote,
            shareSettingsForNoteBeingEdited,
            setShareSettingsForNoteBeingEdited,
            sharePanelOpen,
            setSharePanelOpen,
            openShareForNoteId,
            setOpenShareForNoteId,
            newNoteDraft,
            setNewNoteDraft,
            editingNote,
            setEditingNote,
            isInEditMode,
            setIsInEditMode,
            notes,
            setNotes,
            selectedNote,
            disableTranslation
        }),
        [
            disableTranslation,
            editingNote,
            entity,
            isInEditMode,
            newNoteDraft,
            notes,
            openShareForNoteId,
            pendingShareSettingsForNewNote,
            selectedNote,
            sharePanelOpen,
            shareSettingsForNoteBeingEdited
        ]
    );

    return (
        <NotesContext value={value}>
            <EntitySecurityProvider entityName={ENTITY_CONSTANTS.note} entitySecurity={selectedNote?.security ?? {}}>
                <ShareSettingsProvider throwOnError client={client}>
                    {children}
                </ShareSettingsProvider>
            </EntitySecurityProvider>
        </NotesContext>
    );
}
